import { get, post, put, patch, del } from "./client";
import { ApiEndpoint } from "./endpoints";
import { getUnixTimestamp, setDateToMidnight } from "../util/date-utility";

export interface Reservation {
	id: string;
	tableId: string;
	inquiryId?: string;
	date: Date;
	peopleCount: number;
	primaryName: string;
	time: Date;
	phoneNumber: string;
	email: string;
	notes: string;
	meals?: { [key: string]: number };
	inquiredArea?: string;
	checkedIn?: boolean;
	street?: string;
	zip?: string;
	city?: string;
	country?: string;
}

export interface GetReservationsResponse {
	reservations: Reservation[];
}

export type AddReservationsResponse = Reservation[];

export const transformReservationResponse = <T extends Partial<Reservation>>(
	data: T
): T => {
	if (typeof data.date === "string") {
		data.date = new Date(data.date);
	}

	if (data.time && data.time.toString().substr(0, 4) === "0001") {
		data.time = null;
	} else if (typeof data.time === "string") {
		data.time = new Date(data.time);
	}

	return data;
};

/**
 * @deprecated
 */
export const getReservations = async (): Promise<GetReservationsResponse> => {
	const r = await get(ApiEndpoint.Reservations);

	const finalResult: GetReservationsResponse = {
		reservations: [],
	};

	r.forEach((singleRes) => {
		finalResult.reservations = finalResult.reservations.concat(
			...singleRes.reservations
		);
	});

	return finalResult;
};

export const getReservationsByDay = async (
	date: Date
): Promise<Reservation[]> => {
	try {
		const r = (await get(ApiEndpoint.Reservations, null, {
			day: getUnixTimestamp(date, true),
		})) as [Reservation[]];
		return r.reduce((acc, result) => {
			return acc.concat(
				result.map((res) => transformReservationResponse(res))
			);
		}, []);
	} catch (_) {
		return [];
	}
};

export const getAllReservationInquiries = async (): Promise<Reservation[]> => {
	const r = (await get(ApiEndpoint.ReservationInquiries)) as [Reservation[]];

	return r.reduce((acc, result) => {
		return acc.concat(
			result.map((res) => transformReservationResponse(res))
		);
	}, []);
};

export const getReservationInquiriesByDateRange = async (
	start: Date,
	end: Date
): Promise<Reservation[]> => {
	start.setHours(0, 0, 0, 0);
	end.setHours(23, 59, 59, 999);

	const r = (await get(ApiEndpoint.ReservationInquiries, null, {
		from: getUnixTimestamp(start),
		to: getUnixTimestamp(end),
	})) as [Reservation[]];

	return r.reduce((acc, result) => {
		return acc.concat(
			result.map((res) => transformReservationResponse(res))
		);
	}, []);
};

export const addReservations = async (
	reservations: Reservation[]
): Promise<AddReservationsResponse> => {
	if (reservations.length === 0) return null;

	const response = await Promise.all(
		reservations.map((reservation) => {
			delete reservation.id;

			return post(ApiEndpoint.Reservations, {
				...reservation,
				date: setDateToMidnight(reservation.date),
			});
		})
	);

	return response.map((res) => transformReservationResponse(res));
};

export const updateReservations = async (reservations: Reservation[]) => {
	if (reservations.length === 0) return null;

	const res = (await Promise.all(
		reservations.map((reservation) => {
			if (!reservation.id) {
				throw new Error("Cannot update reservation without an ID");
			}

			return put(ApiEndpoint.Reservations, reservation.id, reservation);
		})
	)) as Reservation[];

	return res.map((r) => transformReservationResponse(r));
};

export const patchReservation = async (
	id: string,
	updateData: Partial<Reservation>
) => {
	const r = (await patch(
		ApiEndpoint.Reservations,
		id,
		updateData
	)) as Reservation;

	return transformReservationResponse(r);
};

export const removeReservations = async (ids: string[]) => {
	if (ids.length === 0) return [];

	return await Promise.all(
		ids.map((id) => del(ApiEndpoint.Reservations, id))
	);
};
